/**
 * Report Type Definitions
 * Configurations and metadata for different report types
 */

import { 
  DocumentTextIcon, 
  ChartBarIcon,
  UserGroupIcon,
  TruckIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ClockIcon,
  BanknotesIcon,
  UsersIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';

export const REPORT_CATEGORIES = {
  RENTAL: 'rental',
  SERVICE: 'service',
  GENERAL: 'general'
};

export const REPORT_FORMATS = {
  HTML: 'html',
  PDF: 'pdf',
  CSV: 'csv',
  EXCEL: 'excel',
  JSON: 'json'
};

export const REPORT_FREQUENCIES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
  CUSTOM: 'custom'
};

export const REPORT_TYPE_DEFINITIONS = {
  'daily-log': {
    id: 'daily-log',
    name: 'Daily Rental Log',
    description: 'Detailed log of all item reservations for a specific date or date range',
    category: REPORT_CATEGORIES.RENTAL,
    icon: DocumentTextIcon,
    endpoint: '/api/reports/daily-log',
    frequency: REPORT_FREQUENCIES.DAILY,
    supportedFormats: [REPORT_FORMATS.HTML, REPORT_FORMATS.PDF, REPORT_FORMATS.CSV],
    parameters: {
      startDate: {
        type: 'date',
        required: true,
        label: 'Start Date',
        defaultValue: () => new Date().toISOString().split('T')[0]
      },
      endDate: {
        type: 'date',
        required: false,
        label: 'End Date (Optional)'
      }
    },
    columns: [
      { key: 'reservationNumber', label: 'Reservation #', width: '140px' },
      { key: 'customer', label: 'Customer', width: '140px' },
      { key: 'equipment', label: 'Equipment', width: '120px' },
      { key: 'startTime', label: 'Start Time', width: '180px' },
      { key: 'endTime', label: 'End Time', width: '180px' },
      { key: 'totalDuration', label: 'Total Duration', width: '120px' },
      { key: 'status', label: 'Status', width: '100px' },
      { key: 'totalAmount', label: 'Total Amount', width: '110px', format: 'currency' },
      { key: 'cashAmount', label: 'Cash Amount', width: '110px', format: 'currency' },
      { key: 'creditAmount', label: 'Credit Amount', width: '110px', format: 'currency' },
      { key: 'surchargeAmount', label: 'Surcharge Amount', width: '130px', format: 'currency' }
    ],
    summaryFields: [
      { key: 'totalReservations', label: 'Total Reservations' },
      { key: 'activeReservations', label: 'Active' },
      { key: 'completedReservations', label: 'Completed' },
      { key: 'totalRevenue', label: 'Total Revenue', format: 'currency' }
    ]
  },

  'revenue': {
    id: 'revenue',
    name: 'Rental Revenue Report',
    description: 'Revenue analysis for item rentals with date ranges',
    category: REPORT_CATEGORIES.RENTAL,
    icon: CurrencyDollarIcon,
    endpoint: '/api/reports/revenue',
    frequency: REPORT_FREQUENCIES.CUSTOM,
    supportedFormats: [REPORT_FORMATS.HTML, REPORT_FORMATS.PDF, REPORT_FORMATS.EXCEL],
    parameters: {
      startDate: {
        type: 'date',
        required: false,
        label: 'Start Date (Optional)'
      },
      endDate: {
        type: 'date',
        required: false,
        label: 'End Date (Optional)'
      }
    },
    summaryFields: [
      { key: 'expectedRevenue', label: 'Expected Revenue', format: 'currency' },
      { key: 'expectedCashPayments', label: 'Expected Cash Payments', format: 'currency' },
      { key: 'expectedCreditPayments', label: 'Expected Credit Payments', format: 'currency' },
      { key: 'expectedSurchargePayments', label: 'Expected Surcharge Payments', format: 'currency' },
      { key: 'totalReservations', label: 'Total Reservations' },
      { key: 'paidReservations', label: 'Paid Reservations' },
      { key: 'unpaidReservations', label: 'Unpaid Reservations' }
    ]
  },

  'employee-performance': {
    id: 'employee-performance',
    name: 'Employee Performance',
    description: 'Performance metrics for all employees',
    category: REPORT_CATEGORIES.GENERAL,
    icon: UserGroupIcon,
    endpoint: '/api/reports/employee-performance',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    supportedFormats: [REPORT_FORMATS.HTML, REPORT_FORMATS.PDF, REPORT_FORMATS.EXCEL],
    parameters: {
      startDate: { 
        type: 'date', 
        required: true, 
        label: 'Start Date',
        defaultValue: () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
      },
      endDate: { 
        type: 'date', 
        required: true, 
        label: 'End Date',
        defaultValue: () => new Date().toISOString().split('T')[0]
      }
    },
    columns: [
      { key: 'employee', label: 'Employee', width: '200px' },
      { key: 'totalReservations', label: 'Total Reservations', width: '150px' },
      { key: 'completedReservations', label: 'Completed', width: '120px' },
      { key: 'totalRevenue', label: 'Total Revenue', width: '120px', format: 'currency' },
      { key: 'averageRevenue', label: 'Average Revenue', width: '130px', format: 'currency' },
      { key: 'completionRate', label: 'Completion Rate', width: '120px', format: 'percentage' }
    ],
    summaryFields: [
      { key: 'totalEmployees', label: 'Total Employees' },
      { key: 'totalReservations', label: 'Total Reservations' },
      { key: 'totalRevenue', label: 'Total Revenue', format: 'currency' },
      { key: 'averageRevenue', label: 'Average Revenue', format: 'currency' }
    ]
  },

  'cart-utilization': {
    id: 'cart-utilization',
    name: 'Equipment Utilization',
    description: 'Utilization rates and revenue per equipment item',
    category: REPORT_CATEGORIES.RENTAL,
    icon: TruckIcon,
    endpoint: '/api/reports/cart-utilization',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    supportedFormats: [REPORT_FORMATS.HTML, REPORT_FORMATS.PDF, REPORT_FORMATS.EXCEL],
    parameters: {
      startDate: { 
        type: 'date', 
        required: true, 
        label: 'Start Date',
        defaultValue: () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
      },
      endDate: { 
        type: 'date', 
        required: true, 
        label: 'End Date',
        defaultValue: () => new Date().toISOString().split('T')[0]
      }
    },
    summaryFields: [
      { key: 'totalCarts', label: 'Total Equipment' },
      { key: 'totalReservations', label: 'Total Reservations' },
      { key: 'totalRevenue', label: 'Total Revenue', format: 'currency' },
      { key: 'averageUtilization', label: 'Average Utilization', format: 'percentage' }
    ]
  },

  'service-revenue': {
    id: 'service-revenue',
    name: 'Service Revenue Report',
    description: 'Revenue analysis for service reservations with optional service filtering and date ranges',
    category: REPORT_CATEGORIES.SERVICE,
    icon: CurrencyDollarIcon,
    endpoint: '/api/reports/service-revenue',
    frequency: REPORT_FREQUENCIES.CUSTOM,
    supportedFormats: [REPORT_FORMATS.HTML, REPORT_FORMATS.PDF, REPORT_FORMATS.EXCEL],
    parameters: {
      serviceId: {
        type: 'select',
        required: false,
        label: 'Service (Optional)',
        options: 'services'
      },
      startDate: {
        type: 'date',
        required: false,
        label: 'Start Date (Optional)'
      },
      endDate: {
        type: 'date',
        required: false,
        label: 'End Date (Optional)'
      }
    },
    summaryFields: [
      { key: 'expectedRevenue', label: 'Expected Revenue', format: 'currency' },
      { key: 'actualRevenue', label: 'Actual Revenue', format: 'currency' },
      { key: 'revenueVariance', label: 'Revenue Variance', format: 'currency' },
      { key: 'revenueVariancePercentage', label: 'Variance %', format: 'percentage' },
      { key: 'totalReservations', label: 'Total Reservations' },
      { key: 'paidReservations', label: 'Paid Reservations' },
      { key: 'unpaidReservations', label: 'Unpaid Reservations' },
      { key: 'averageExpectedRevenue', label: 'Avg Expected Revenue', format: 'currency' },
      { key: 'averageActualRevenue', label: 'Avg Actual Revenue', format: 'currency' }
    ]
  },

  'service-attendees': {
    id: 'service-attendees',
    name: 'Service Attendee Report',
    description: 'Comprehensive service reservation details with customer information, questionnaire responses, and add-ons',
    category: REPORT_CATEGORIES.SERVICE,
    icon: TableCellsIcon,
    endpoint: '/api/reports/service-attendees',
    frequency: REPORT_FREQUENCIES.CUSTOM,
    supportedFormats: [REPORT_FORMATS.HTML, REPORT_FORMATS.EXCEL, REPORT_FORMATS.CSV],
    parameters: {
      startDate: {
        type: 'date',
        required: false,
        label: 'Start Date (Optional)'
      },
      endDate: {
        type: 'date',
        required: false,
        label: 'End Date (Optional)'
      },
      serviceId: {
        type: 'select',
        required: false,
        label: 'Specific Service (Optional)',
        options: 'services' // Will be populated dynamically
      },
      excludeCancelled: {
        type: 'checkbox',
        required: false,
        label: 'Exclude Cancelled',
        defaultValue: false
      }
    },
    dynamicColumns: true, // Indicates columns are generated dynamically based on data
    baseColumns: [
      { key: 'serviceName', label: 'Service', width: '100px' },
      { key: 'customerFirstName', label: 'First Name', width: '80px' },
      { key: 'customerLastName', label: 'Last Name', width: '80px' },
      { key: 'reservationId', label: 'Res ID', width: '80px' },
      { key: 'phoneNumber', label: 'Phone', width: '90px' },
      { key: 'participantCount', label: '# of Participants', width: '60px' },
      { key: 'emailAddress', label: 'Email', width: '120px' },
      { key: 'paymentStatus', label: 'Payment', width: '70px' },
      { key: 'amount', label: 'Amount', width: '70px', format: 'currency' },
      { key: 'source', label: 'Source', width: '60px' }
    ],
    summaryFields: [
      { key: 'totalAttendees', label: 'Total Attendees' },
      { key: 'totalServices', label: 'Total Services' },
      { key: 'totalParticipants', label: 'Total Participants' },
      { key: 'totalRevenue', label: 'Total Revenue', format: 'currency' }
    ]
  }
};

export const REPORT_STATUS = {
  PENDING: 'pending',
  GENERATING: 'generating',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
};

export const FIELD_FORMATS = {
  CURRENCY: 'currency',
  PERCENTAGE: 'percentage',
  NUMBER: 'number',
  DATE: 'date',
  DATETIME: 'datetime',
  TEXT: 'text'
};

// Helper functions
export const getReportTypeById = (id) => {
  return REPORT_TYPE_DEFINITIONS[id] || null;
};

export const getReportTypesByCategory = (category) => {
  return Object.values(REPORT_TYPE_DEFINITIONS)
    .filter(report => report.category === category);
};

export const getAllReportTypes = () => {
  return Object.values(REPORT_TYPE_DEFINITIONS);
};

export const getDefaultParameters = (reportType) => {
  const reportDef = getReportTypeById(reportType);
  if (!reportDef) return {};

  const defaults = {};
  Object.entries(reportDef.parameters).forEach(([key, param]) => {
    if (param.defaultValue) {
      defaults[key] = typeof param.defaultValue === 'function' 
        ? param.defaultValue() 
        : param.defaultValue;
    }
  });

  return defaults;
};